import { memo } from "react";
import { generateDemoData } from "@/utils/mockUtils";
import type { AgentActionHistory } from "@/utils/models";
import { DemoAgent } from "../RequestData/NotificationsHistory/DemoAgent";

const demoAgentDataCache = new Map<string | number, AgentActionHistory[]>();

const renderedMessages = new Set<string | number>();

export const StableDemoAgent = memo(
	({
		messageId,
		messageTimestamp,
		eligibleNursesCount,
		hideFuture,
	}: {
		messageId: string | number;
		messageTimestamp: string;
		eligibleNursesCount?: number;
		hideFuture?: boolean;
	}) => {
		const cacheKey = `${messageId}-${eligibleNursesCount ?? "default"}`;

		const existingCache = demoAgentDataCache.get(cacheKey);
		const cachedCount = existingCache?.[0]?.eligible_nurses_count;
		const cacheExistedBefore = !!existingCache;

		if (!existingCache || (eligibleNursesCount !== undefined && cachedCount !== eligibleNursesCount)) {
			demoAgentDataCache.set(cacheKey, generateDemoData(7, "hours", eligibleNursesCount));
		}

		const skipLoading = cacheExistedBefore;
		const cachedData = demoAgentDataCache.get(cacheKey);
		const hasBeenRendered = renderedMessages.has(messageId);

		// Only skip animation if this message has been rendered before
		// Don't skip based on cacheExistedBefore because that's for future data, not state
		const shouldSkipAnimation = hasBeenRendered;

		// Mark this message as rendered (but only after first render completes)
		// This ensures animation plays on first render
		if (!hasBeenRendered) {
			renderedMessages.add(messageId);
		}

		return (
			<DemoAgent
				key={`stable-demo-${messageId}-${messageTimestamp}`}
				daysFromNow={7}
				skipInitialLoading={skipLoading}
				cachedFutureData={cachedData}
				datePart="hours"
				eligibleNursesCount={eligibleNursesCount}
				cacheKey={cacheKey}
				skipAnimation={shouldSkipAnimation}
				hideFuture={hideFuture}
			/>
		);
	},
	(prevProps, nextProps) =>
		prevProps.messageId === nextProps.messageId &&
		prevProps.messageTimestamp === nextProps.messageTimestamp &&
		prevProps.eligibleNursesCount === nextProps.eligibleNursesCount &&
		prevProps.hideFuture === nextProps.hideFuture,
);
